import React, { useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, TextField, Typography, Avatar, Grid,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import { getProfile, updateProfile } from "../services/userService";

interface ProfileDto {
  userId: number;
  userName: string;
  userEmail: string;
  phone: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+90|0)?5\d{9}$/;

const filledSx = {
  "& .MuiFilledInput-root": {
    borderRadius: "8px",
    backgroundColor: "#f4f6f8",
    "&:before": { display: "none" },
    "&:after": { display: "none" },
  },
  "& .MuiFilledInput-root.Mui-disabled": {
    backgroundColor: "#f4f6f8",
  },
  "& .MuiInputLabel-root": { fontSize: 13 },
};

const Profile: React.FC = () => {
  const { showSuccess, showError, snackbarProps } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [saved, setSaved] = useState({ userName: "", userEmail: "", phone: "" });
  const [form, setForm] = useState({ userName: "", userEmail: "", phone: "" });
  const [errors, setErrors] = useState<{ userName?: string; userEmail?: string; phone?: string }>({});

  useEffect(() => {
    getProfile()
      .then((res) => {
        const data: ProfileDto = (res.data as any).data;
        const vals = { userName: data.userName, userEmail: data.userEmail ?? "", phone: data.phone ?? "" };
        setUserId(data.userId);
        setSaved(vals);
        setForm(vals);
      })
      .catch((e) => showError(e as Error))
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = () => {
    setForm(saved);
    setErrors({});
    setEditing(false);
  };

  const handleSave = async () => {
    const errs: typeof errors = {};
    if (!form.userName.trim()) errs.userName = "Kullanıcı adı boş olamaz.";
    if (!form.userEmail.trim()) errs.userEmail = "E-posta boş olamaz.";
    else if (!EMAIL_REGEX.test(form.userEmail)) errs.userEmail = "Geçersiz e-posta formatı.";
    if (form.phone && !PHONE_REGEX.test(form.phone.replace(/\s|-/g, ""))) errs.phone = "Geçersiz telefon. Örnek: 05xx1234567";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    try {
      await updateProfile(form);
      setSaved(form);
      setEditing(false);
      setErrors({});
      showSuccess("Profil güncellendi.");
    } catch (e) {
      showError(e as Error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="panel-container">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, p: { xs: 3, md: 4 } }}>
            {/* Page header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{ width: 52, height: 52, fontSize: 22, fontWeight: 700, bgcolor: "var(--color-primary)" }}
                >
                  {saved.userName.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography fontSize={20} fontWeight={700} color="text.primary">
                    {saved.userName}
                  </Typography>
                  <Typography fontSize={13} color="text.secondary">{saved.userEmail}</Typography>
                </Box>
              </Box>
              {!editing && userId !== 1 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setEditing(true)}
                  sx={{ textTransform: "none", borderRadius: "8px", fontSize: 13 }}
                >
                  Düzenle
                </Button>
              )}
            </Box>

            <Box sx={{ borderTop: "1px solid #f0f0f0", pt: 3 }}>
              {/* Section title */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                <AccountCircleOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography fontSize={15} fontWeight={600} color="text.primary">
                  Hesap Bilgileri
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Kullanıcı Adı"
                    variant="filled"
                    fullWidth
                    size="small"
                    value={form.userName}
                    onChange={(e) => { setForm({ ...form, userName: e.target.value }); setErrors((p) => ({ ...p, userName: undefined })); }}
                    disabled={!editing}
                    error={!!errors.userName}
                    helperText={errors.userName}
                    sx={filledSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="E-posta"
                    variant="filled"
                    fullWidth
                    size="small"
                    value={form.userEmail}
                    onChange={(e) => { setForm({ ...form, userEmail: e.target.value }); setErrors((p) => ({ ...p, userEmail: undefined })); }}
                    disabled={!editing}
                    error={!!errors.userEmail}
                    helperText={errors.userEmail}
                    sx={filledSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Telefon"
                    variant="filled"
                    fullWidth
                    size="small"
                    value={form.phone}
                    onChange={(e) => { setForm({ ...form, phone: e.target.value }); setErrors((p) => ({ ...p, phone: undefined })); }}
                    disabled={!editing}
                    error={!!errors.phone}
                    helperText={errors.phone ?? (editing ? "Örnek: 05xx1234567" : undefined)}
                    sx={filledSx}
                  />
                </Grid>
              </Grid>

              {editing && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3, pt: 2.5, borderTop: "1px solid #f0f0f0" }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{ textTransform: "none", borderRadius: "8px" }}
                  >
                    İptal
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      bgcolor: "var(--color-primary)",
                      "&:hover": { bgcolor: "var(--color-primary-dark)" },
                      px: 3,
                    }}
                  >
                    Kaydet
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </div>
      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Profile;
